import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Grid
} from '@mui/material';
import {
  Gavel,
  Security,
  VerifiedUser,
  Assignment,
  CheckCircle,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo0.png';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#000000', p: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={logo} 
                alt="Logo" 
                style={{ 
                  height: 40,
                  filter: 'brightness(0) invert(1)',
                  cursor: 'pointer'
                }} 
                onClick={() => navigate('/')}
              />
              <Typography variant="h6" sx={{ ml: 2, color: 'white', fontWeight: 600 }}>
                Terms of Service
              </Typography>
            </Box>
            <Button 
              startIcon={<ArrowBack />}
              sx={{ color: '#FFE302' }}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: '#000', 
        color: 'white',
        py: 6,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,227,2,0.3) 0%, rgba(255,227,2,0) 70%)',
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,227,2,0.2) 0%, rgba(255,227,2,0) 70%)',
        }} />
        <Container maxWidth="lg">
          <Grid container alignItems="center" spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: '#FFE302' }}>
                Terms of Service
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                Please read these terms carefully before using our platform. By accessing or using our services, you agree to be bound by these terms and conditions.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" sx={{ opacity: 0.7 }}>
                  Last Updated: June 15, 2023
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Typography variant="body1" sx={{ opacity: 0.7 }}>
                  Effective: July 1, 2023
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Gavel sx={{ fontSize: 180, color: 'rgba(255,227,2,0.2)' }} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                1. Acceptance of Terms
              </Typography>
              <Typography paragraph>
                By accessing or using Larravaris AI's services, website, or applications (collectively, the "Services"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Services.
              </Typography>
              <Typography paragraph>
                We reserve the right to modify these terms at any time. Your continued use of the Services following the posting of changes constitutes your acceptance of such changes. It is your responsibility to review these Terms of Service periodically.
              </Typography>
            </Paper>

            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                2. Service Description
              </Typography>
              <Typography paragraph>
                Larravaris AI provides AI-powered diagnostic and repair services for electronic devices. Our platform allows users to upload images and information about their devices to receive automated diagnostics, repair suggestions, and connections to professional repair services.
              </Typography>
              <Typography paragraph>
                While we strive for accuracy, our AI-based diagnostics are provided on an "as is" and "as available" basis. The accuracy of our diagnostics depends on the quality of information provided by users and the current capabilities of our AI systems.
              </Typography>
            </Paper>

            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                3. User Accounts and Responsibilities
              </Typography>
              <Typography paragraph>
                To access certain features of our Services, you may need to create an account. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account.
              </Typography>
              <Typography paragraph>
                You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete. We reserve the right to suspend or terminate your account if any information provided proves to be inaccurate, not current, or incomplete.
              </Typography>
            </Paper>

            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                4. Privacy and Data Usage
              </Typography>
              <Typography paragraph>
                Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your personal information. By using our Services, you consent to the collection and use of information as detailed in our Privacy Policy.
              </Typography>
              <Typography paragraph>
                We may use anonymized data from device diagnostics to improve our AI systems and services. This data is stripped of personally identifiable information before being used for system improvements.
              </Typography>
            </Paper>

            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                5. Intellectual Property
              </Typography>
              <Typography paragraph>
                All content, features, and functionality of our Services, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, are the exclusive property of Larravaris AI or its licensors and are protected by international copyright, trademark, and other intellectual property laws.
              </Typography>
              <Typography paragraph>
                You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Services without our prior written consent.
              </Typography>
            </Paper>

            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                6. Limitation of Liability
              </Typography>
              <Typography paragraph>
                In no event shall Larravaris AI, its officers, directors, employees, or agents be liable for any indirect, punitive, incidental, special, consequential, or exemplary damages, including without limitation, damages for loss of profits, goodwill, use, data, or other intangible losses, that result from the use of, or inability to use, the Services.
              </Typography>
              <Typography paragraph>
                We do not guarantee that our Services will be uninterrupted, timely, secure, or error-free. You understand and agree that you use our Services at your own discretion and risk.
              </Typography>
            </Paper>

            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                7. Termination
              </Typography>
              <Typography paragraph>
                We may terminate or suspend your account and access to our Services immediately, without prior notice or liability, for any reason, including without limitation if you breach these Terms of Service.
              </Typography>
              <Typography paragraph>
                Upon termination, your right to use the Services will immediately cease. If you wish to terminate your account, you may simply discontinue using the Services or contact us to request account deletion.
              </Typography>
            </Paper>

            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                8. Governing Law
              </Typography>
              <Typography paragraph>
                These Terms shall be governed by and construed in accordance with the laws of Algeria, without regard to its conflict of law provisions.
              </Typography>
              <Typography paragraph>
                Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located within Algeria.
              </Typography>
            </Paper>

            <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                9. Contact Information
              </Typography>
              <Typography paragraph>
                If you have any questions about these Terms, please contact us at:
              </Typography>
              <Typography paragraph sx={{ fontWeight: 500 }}>
                Email: legal@larravaris.com<br />
                Address: 123 Tech Boulevard, Algiers, Algeria<br />
                Phone: +213 123 456 789
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Assignment sx={{ mr: 1, color: '#FFE302' }} /> Table of Contents
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List sx={{ p: 0 }}>
                  {[
                    'Acceptance of Terms',
                    'Service Description',
                    'User Accounts and Responsibilities',
                    'Privacy and Data Usage',
                    'Intellectual Property',
                    'Limitation of Liability',
                    'Termination',
                    'Governing Law',
                    'Contact Information'
                  ].map((item, index) => (
                    <ListItem 
                      key={index} 
                      button 
                      sx={{ 
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: 'rgba(255, 227, 2, 0.1)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                          {index + 1}.
                        </Typography>
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Security sx={{ mr: 1, color: '#FFE302' }} /> Your Data Protection
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List sx={{ p: 0 }}>
                  {[
                    'We encrypt all personal data',
                    'Your device images are stored securely',
                    'We never sell your information',
                    'You can request data deletion anytime'
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ px: 1, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle sx={{ color: '#00a651', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', bgcolor: '#000', color: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', color: '#FFE302' }}>
                  <VerifiedUser sx={{ mr: 1 }} /> Need Help?
                </Typography>
                <Typography paragraph>
                  If you have questions about our terms or need assistance, our support team is here to help.
                </Typography>
                <Button 
                  variant="contained" 
                  fullWidth
                  sx={{ 
                    bgcolor: '#FFE302', 
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#FFD700'
                    }
                  }}
                >
                  Contact Support
                </Button>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#000', color: 'white', py: 4, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                © 2023 Larravaris AI. All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.7, cursor: 'pointer' }}>
                  Privacy Policy
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, cursor: 'pointer', fontWeight: 'bold' }}>
                  Terms of Service
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, cursor: 'pointer' }}>
                  Cookie Policy
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Terms; 